// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from 'common/models/flow/ImmutableModel'; // Make sure you copied this file into your repo

import { AuditMetadata } from 'common/models/flow/AuditMetadata';
import { StructuredAddress } from 'common/models/flow/StructuredAddress';
import { TeamId } from 'common/models/flow/TeamId';
import { LatLng } from 'common/models/flow/LatLng';
import { ContactId } from 'common/models/flow/ContactId';
import { CrossDockInfo } from './CrossDockInfo';
import { MultiDaySlot } from 'common/models/flow/MultiDaySlot';
import { Contact } from 'common/models/flow/Contact';

import type { AuditMetadataModelType } from 'common/models/flow/AuditMetadata';
import type { TeamIdModelType } from 'common/models/flow/TeamId';
import type { ContactModelType } from 'common/models/flow/Contact';
import type { ContactIdModelType } from 'common/models/flow/ContactId';
import type { StructuredAddressModelType } from 'common/models/flow/StructuredAddress';
import type { LatLngModelType } from 'common/models/flow/LatLng';
import type { MultiDaySlotModelType } from 'common/models/flow/MultiDaySlot';
import type { CrossDockInfoModelType } from 'common/models/flow/CrossDockInfo';

export type HomebaseStatusEnum = 'ACTIVE' | 'INACTIVE';
export type HomebaseTypeEnum = 'HOMEBASE' | 'CROSS_DOCK';

export type MasterHomebaseModelType = {
    clientId: string,
    id: string,
    code: string,
    name: string,
    status: HomebaseStatusEnum,
    type: HomebaseTypeEnum,
    teams: Array<TeamIdModelType>,
    contact: ?ContactModelType,
    contactId: ?ContactIdModelType,
    address: StructuredAddressModelType,
    latLng: LatLngModelType,
    transactionDuration: number,
    timeSlots: Array<MultiDaySlotModelType>,
    crossDockInfo: ?CrossDockInfoModelType,
    customProperties: { [key: string]: string },
    auditMetadata: ?AuditMetadataModelType,
    version: number
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class MasterHomebase extends ImmutableModel {
    static fromJS(json: MasterHomebaseModelType): MasterHomebase {
        const state: Object = Object.assign({}, json);
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.contact = state.contact
            ? Contact.fromJS(state.contact)
            : state.contact;
        state.contactId = state.contactId
            ? ContactId.fromJS(state.contactId)
            : state.contactId;
        state.address = StructuredAddress.fromJS(state.address);
        state.latLng = LatLng.fromJS(state.latLng);
        state.timeSlots = Immutable.List(state.timeSlots).map(item =>
            MultiDaySlot.fromJS(item)
        );
        state.crossDockInfo = state.crossDockInfo
            ? CrossDockInfo.fromJS(state.crossDockInfo)
            : state.crossDockInfo;
        state.customProperties = Immutable.Map(state.customProperties);
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        return new this(Immutable.Map(state));
    }

    toJS(): MasterHomebaseModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            code: this.code,
            name: this.name,
            status: this.status,
            type: this.type,
            teams: this.teams.toArray().map(item => item.toJS()),
            contact: this.contact ? this.contact.toJS() : this.contact,
            contactId: this.contactId ? this.contactId.toJS() : this.contactId,
            address: this.address.toJS(),
            latLng: this.latLng.toJS(),
            transactionDuration: this.transactionDuration,
            timeSlots: this.timeSlots.toArray().map(item => item.toJS()),
            crossDockInfo: this.crossDockInfo
                ? this.crossDockInfo.toJS()
                : this.crossDockInfo,
            customProperties: this.customProperties.toObject(),
            auditMetadata: this.auditMetadata
                ? this.auditMetadata.toJS()
                : this.auditMetadata,
            version: this.version
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get status(): HomebaseStatusEnum {
        return this._state.get('status');
    }

    setStatus(status: HomebaseStatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get type(): HomebaseTypeEnum {
        return this._state.get('type');
    }

    setType(type: HomebaseTypeEnum): this {
        return this.clone(this._state.set('type', type));
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    setTeams(teams: Immutable.List<TeamId>): this {
        return this.clone(this._state.set('teams', teams));
    }

    get contact(): ?Contact {
        return this._state.get('contact');
    }

    setContact(contact: ?Contact): this {
        return this.clone(this._state.set('contact', contact));
    }

    get contactId(): ?ContactId {
        return this._state.get('contactId');
    }

    setContactId(contactId: ?ContactId): this {
        return this.clone(this._state.set('contactId', contactId));
    }

    get address(): StructuredAddress {
        return this._state.get('address');
    }

    setAddress(address: StructuredAddress): this {
        return this.clone(this._state.set('address', address));
    }

    get latLng(): LatLng {
        return this._state.get('latLng');
    }

    setLatLng(latLng: LatLng): this {
        return this.clone(this._state.set('latLng', latLng));
    }

    get transactionDuration(): number {
        return this._state.get('transactionDuration');
    }

    setTransactionDuration(transactionDuration: number): this {
        return this.clone(
            this._state.set('transactionDuration', transactionDuration)
        );
    }

    get timeSlots(): Immutable.List<MultiDaySlot> {
        return this._state.get('timeSlots');
    }

    setTimeSlots(timeSlots: Immutable.List<MultiDaySlot>): this {
        return this.clone(this._state.set('timeSlots', timeSlots));
    }

    get crossDockInfo(): ?CrossDockInfo {
        return this._state.get('crossDockInfo');
    }

    setCrossDockInfo(crossDockInfo: ?CrossDockInfo): this {
        return this.clone(this._state.set('crossDockInfo', crossDockInfo));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: ?AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }
}
